import type { ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Table.module.css';

export type TableColumn<T> = {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  sortable?: boolean;
};

export type TableProps<T> = {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onSort?: (key: string) => void;
  empty?: ReactNode;
  /** Merged onto the outer wrap (e.g. overflow overrides). */
  className?: string;
};

type TableWrapperProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Two modes:
 * 1. Prop-based: pass `columns` + `rows` → renders full table.
 * 2. Children-based: pass raw `<thead>`/`<tbody>` as children → styled wrapper only.
 */
export function Table<T>(props: TableProps<T> | TableWrapperProps) {
  if ('children' in props) {
    return (
      <div className={cx(styles.tableWrap, props.className)}>
        <table className={styles.table}>{props.children}</table>
      </div>
    );
  }

  const { columns, rows, rowKey, onSort, empty, className } = props;
  if (!rows || rows.length === 0) {
    return <div className={cx(styles.tableWrap, className)}>{empty ?? null}</div>;
  }
  return (
    <div className={cx(styles.tableWrap, className)}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={cx(styles.th, c.sortable && styles.thSortable)} onClick={c.sortable && onSort ? () => onSort(c.key) : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className={styles.tr}>
              {columns.map((c) => (
                <td key={c.key} className={styles.td}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
